import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { BiPlus, BiSave, BiTrash, BiX } from 'react-icons/bi';

import Bouton from '../../UI/Bouton/Bouton';
import ChampDeTexte from '../../UI/ChampDeTexte/ChampDeTexte';
import Collapse from '../../../hoc/Collapse/Collapse';

import { generateId } from '../../../ressources/functionJS/generateId';
import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import { getFunctionById, getVEnvs } from '../../../services/api';
import S from './FunctionBuilder.module.scss';

const ARGUMENT_TYPES = [
  { value: 'String', label: 'Texte' },
  { value: 'Number', label: 'Nombre' },
  { value: 'DateOnly', label: 'Date seule' },
  { value: 'DateTime', label: 'Date et heure' },
  { value: 'DateRange', label: 'Plage de dates' },
  { value: 'Mode', label: 'Mode (Select)' },
  { value: 'SensorList', label: 'Liste de capteurs' },
  { value: 'DevuiList', label: 'Liste DevEUI' },
  { value: 'Sensor', label: 'Capteur unique' },
];

const getVEnvOptionLabel = (venv) => {
  if (!venv) return '';
  return `${venv.name}`;
};

const FunctionBuilder = ({
  initialFunction = null,
  isEditMode = false,
  annuler,
  sauvegarderFunction,
  readOnly = false,
}) => {
  const [functionData, setFunctionData] = useState({
    id_function: null,
    name: '',
    description: '',
    venv_id: '',
    arguments_definition: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadingVEnvs, setLoadingVEnvs] = useState(false);
  const [erreurs, setErreurs] = useState({});
  const [venvs, setVEnvs] = useState([]);
  const [venvInputValue, setVenvInputValue] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadVEnvs = async () => {
      setLoadingVEnvs(true);
      try {
        const response = await getVEnvs();
        const responseData = response;
        const apiVenvs = responseData?.venvs || responseData || [];

        const normalizedVEnvs = Array.isArray(apiVenvs)
          ? apiVenvs.map((venv) => ({
              id: venv.id,
              name: venv.name || `venv-${venv.id}`,
              description: venv.description || '',
              path: venv.path || '',
            }))
          : [];

        if (mounted) {
          setVEnvs(normalizedVEnvs);
        }
      } catch (err) {
        console.error('Error loading venvs:', err);
        if (mounted) {
          setVEnvs([]);
        }
      } finally {
        if (mounted) {
          setLoadingVEnvs(false);
        }
      }
    };

    loadVEnvs();

    return () => {
      mounted = false;
    };
  }, []);

  // Charger les données de la fonction
  useEffect(() => {
    if (initialFunction) {
      if (initialFunction.id_function) {
        setLoading(true);
        getFunctionById({ functionId: initialFunction.id_function })
          .then((response) => {
            const data = response;
            if (data && data.arguments_definition) {
              setFunctionData({
                id_function: data.idFunction || initialFunction.id_function,
                name: data.name || '',
                description: data.description || '',
                venv_id: data.venv_id || data.venvId || data.venv?.id || '',
                arguments_definition: data.arguments_definition.map(
                  (arg, idx) => ({
                    id: generateId(),
                    position_in_arg_list: arg.position_in_arg_list || idx + 1,
                    type: arg.type || 'String',
                    title: arg.title || '',
                    description: arg.description || '',
                    options: arg.options || [],
                  })
                ),
              });
            }
          })
          .catch((err) => {
            console.error('Error loading function details:', err);
            alert('Erreur lors du chargement des détails de la fonction');
          })
          .finally(() => setLoading(false));
      }
      // Mode création
      else {
        setFunctionData({
          id_function: null,
          name: '',
          description: '',
          venv_id: '',
          arguments_definition: [],
        });
      }
    }
  }, [initialFunction, isEditMode]);

  useEffect(() => {
    const selectedVEnv = venvs.find(
      (venv) => String(venv.id) === String(functionData.venv_id)
    );

    if (selectedVEnv) {
      setVenvInputValue(getVEnvOptionLabel(selectedVEnv));
      return;
    }

    if (!functionData.venv_id) {
      setVenvInputValue('');
    }
  }, [functionData.venv_id, venvs]);

  const handleChange = useCallback(
    (field, value) => {
      if (readOnly) return;
      setFunctionData((prev) => ({ ...prev, [field]: value }));
      setErreurs((prev) => {
        if (prev[field]) {
          const { [field]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    [readOnly]
  );

  const handleArgumentChange = useCallback(
    (argId, field, value) => {
      if (readOnly) return;
      setFunctionData((prev) => ({
        ...prev,
        arguments_definition: prev.arguments_definition.map((arg) =>
          arg.id === argId ? { ...arg, [field]: value } : arg
        ),
      }));
    },
    [readOnly]
  );

  const handleAddArgument = useCallback(() => {
    if (readOnly) return;
    const newArg = {
      id: generateId(),
      position_in_arg_list: functionData.arguments_definition.length + 1,
      type: 'String',
      title: '',
      description: '',
      options: [],
    };
    setFunctionData((prev) => ({
      ...prev,
      arguments_definition: [...prev.arguments_definition, newArg],
    }));
  }, [functionData.arguments_definition.length, readOnly]);

  const handleRemoveArgument = useCallback(
    (argId) => {
      if (readOnly) return;
      setFunctionData((prev) => ({
        ...prev,
        arguments_definition: prev.arguments_definition
          .filter((arg) => arg.id !== argId)
          .map((arg, idx) => ({ ...arg, position_in_arg_list: idx + 1 })),
      }));
    },
    [readOnly]
  );

  const handleAddOption = useCallback(
    (argId) => {
      if (readOnly) return;
      setFunctionData((prev) => ({
        ...prev,
        arguments_definition: prev.arguments_definition.map((arg) =>
          arg.id === argId
            ? {
                ...arg,
                options: [...(arg.options || []), { key: '', value: '' }],
              }
            : arg
        ),
      }));
    },
    [readOnly]
  );

  const handleRemoveOption = useCallback(
    (argId, optionIndex) => {
      if (readOnly) return;
      setFunctionData((prev) => ({
        ...prev,
        arguments_definition: prev.arguments_definition.map((arg) =>
          arg.id === argId
            ? {
                ...arg,
                options: arg.options.filter((_, idx) => idx !== optionIndex),
              }
            : arg
        ),
      }));
    },
    [readOnly]
  );

  const handleOptionChange = useCallback(
    (argId, optionIndex, field, value) => {
      if (readOnly) return;
      setFunctionData((prev) => ({
        ...prev,
        arguments_definition: prev.arguments_definition.map((arg) =>
          arg.id === argId
            ? {
                ...arg,
                options: arg.options.map((opt, idx) =>
                  idx === optionIndex ? { ...opt, [field]: value } : opt
                ),
              }
            : arg
        ),
      }));
    },
    [readOnly]
  );

  const handleVEnvInputChange = useCallback(
    (value) => {
      if (readOnly) return;

      setVenvInputValue(value);

      const trimmed = value.trim();
      if (!trimmed) {
        handleChange('venv_id', '');
        return;
      }

      const normalizedValue = trimmed.toLowerCase();
      const matchedVEnv = venvs.find((venv) => {
        const optionLabel = getVEnvOptionLabel(venv).toLowerCase();
        const byName = (venv.name || '').toLowerCase();
        const byPath = (venv.path || '').toLowerCase();
        const byId = String(venv.id).toLowerCase();

        return (
          normalizedValue === optionLabel ||
          normalizedValue === byName ||
          normalizedValue === byPath ||
          normalizedValue === byId
        );
      });

      if (matchedVEnv) {
        handleChange('venv_id', String(matchedVEnv.id));
      }
    },
    [handleChange, readOnly, venvs]
  );

  const valider = useCallback(() => {
    if (readOnly) return true;
    const errs = {};
    if (!functionData.name) errs.name = 'Nom de la fonction requis';
    if (!functionData.description) errs.description = 'Description requise';
    setErreurs(errs);
    return Object.keys(errs).length === 0;
  }, [functionData.name, functionData.description, readOnly]);

  const handleSave = () => {
    if (readOnly || !valider()) return;

    // Transform to API spec format
    const payload = {
      name: functionData.name,
      description: functionData.description,
      ...(functionData.venv_id
        ? { venv_id: Number(functionData.venv_id) }
        : {}),
      list: '', // Add list field if needed
      definitions: functionData.arguments_definition.map((arg) => ({
        position: arg.position_in_arg_list,
        type: arg.type,
        title: arg.title || '',
        description: arg.description || '',
        enums: (arg.options || []).map((opt) => ({
          key: opt.key || '',
          value: opt.value || '',
        })),
      })),
    };
    sauvegarderFunction(payload, isEditMode);
  };

  if (loading) {
    return <div className={S.container}>Chargement...</div>;
  }

  const headerTitle = readOnly
    ? 'Détails de la fonction'
    : isEditMode
      ? 'Modifier la fonction'
      : 'Créer une fonction';

  const selectedVEnv = venvs.find(
    (venv) => String(venv.id) === String(functionData.venv_id)
  );

  const filteredVEnvs = venvs.filter((venv) => {
    const searchValue = venvInputValue.trim().toLowerCase();
    if (!searchValue) return true;

    return [venv.name, venv.description, venv.path, String(venv.id)]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(searchValue));
  });

  return (
    <div className={S.container}>
      <div className={S.header}>
        <h3 className={S.titre}>{headerTitle}</h3>
      </div>

      <div className={S.contenu}>
        {/* Informations de la fonction */}
        <Collapse expandText="Informations générales">
          <div className={S.section}>
            <h4 style={{ display: 'none' }}>Informations générales</h4>
            {isEditMode && (
              <div className={S.functionInfo}>
                <div className={S.infoRow}>
                  <span className={S.infoLabel}>ID:</span>
                  <span className={S.infoValue}>
                    {functionData.id_function}
                  </span>
                </div>
              </div>
            )}

            <div className={S.fieldGroup}>
              <label>Nom de la fonction</label>
              <ChampDeTexte
                name="name"
                placeholder="Nom de la fonction"
                type="text"
                value={functionData.name}
                onChange={
                  readOnly ? () => {} : (value) => handleChange('name', value)
                }
                disabled={readOnly}
                required
              />
            </div>

            <div className={S.fieldGroup}>
              <label>Description</label>
              <ChampDeTexte
                name="description"
                placeholder="Description de la fonction"
                type="text"
                value={functionData.description}
                onChange={
                  readOnly
                    ? () => {}
                    : (value) => handleChange('description', value)
                }
                disabled={readOnly}
              />
            </div>

            <div className={S.fieldGroup}>
              <label>Environnement virtuel Python (venv)</label>
              <ChampDeTexte
                name="venv-combo"
                placeholder="Rechercher et selectionner un venv"
                type="text"
                value={venvInputValue}
                onChange={readOnly ? () => {} : handleVEnvInputChange}
                disabled={readOnly || loadingVEnvs}
                list="functionbuilder-venv-list"
              />
              <datalist id="functionbuilder-venv-list">
                {filteredVEnvs.map((venv) => (
                  <option key={venv.id} value={getVEnvOptionLabel(venv)}>
                    {venv.description || venv.path || ''}
                  </option>
                ))}
              </datalist>
              {loadingVEnvs && (
                <small className={S.helperText}>Chargement des venvs...</small>
              )}
              {!loadingVEnvs && venvs.length === 0 && (
                <small className={S.helperText}>Aucun venv disponible.</small>
              )}
              {!loadingVEnvs &&
                venvs.length > 0 &&
                filteredVEnvs.length === 0 && (
                  <small className={S.helperText}>
                    Aucun venv ne correspond a la recherche.
                  </small>
                )}
              {!loadingVEnvs && selectedVEnv?.path && (
                <small className={S.helperText}>{selectedVEnv.path}</small>
              )}
            </div>
          </div>
        </Collapse>
        {/* Arguments */}
        <Collapse
          expandText={`Arguments (${functionData.arguments_definition.length})`}
        >
          <div className={S.section}>
            <div className={S.sectionHeader}>
              <h4 style={{ display: 'none' }}>Arguments</h4>
              {!readOnly && (
                <Bouton
                  type={type_btn.primary}
                  taille="small"
                  cliquer={handleAddArgument}
                >
                  <BiPlus size={16} /> Ajouter un argument
                </Bouton>
              )}
            </div>

            {functionData.arguments_definition.map((arg, idx) => (
              <div key={arg.id} className={S.argumentBlock}>
                <div className={S.argumentContent}>
                  <div className={S.fieldGroup}>
                    <label>Position de l'argument</label>
                    <ChampDeTexte
                      name={`arg-position-${arg.id}`}
                      type="number"
                      value={arg.position_in_arg_list}
                      onChange={
                        readOnly
                          ? () => {}
                          : (value) =>
                              handleArgumentChange(
                                arg.id,
                                'position_in_arg_list',
                                Number(value)
                              )
                      }
                      disabled={readOnly}
                      min={1}
                      style={{ width: '80px' }}
                    />
                  </div>
                  <div className={S.fieldGroup}>
                    <label>Titre</label>
                    <ChampDeTexte
                      name={`arg-title-${arg.id}`}
                      type="text"
                      value={arg.title || ''}
                      onChange={
                        readOnly
                          ? () => {}
                          : (value) =>
                              handleArgumentChange(arg.id, 'title', value)
                      }
                      disabled={readOnly}
                      placeholder="Titre de l'argument"
                    />
                  </div>
                  <div className={S.fieldGroup}>
                    <label>Description</label>
                    <ChampDeTexte
                      name={`arg-description-${arg.id}`}
                      type="text"
                      value={arg.description || ''}
                      onChange={
                        readOnly
                          ? () => {}
                          : (value) =>
                              handleArgumentChange(arg.id, 'description', value)
                      }
                      disabled={readOnly}
                      placeholder="Description de l'argument"
                    />
                  </div>
                  <div className={S.fieldGroup}>
                    <label>Type</label>
                    <select
                      value={arg.type}
                      onChange={
                        readOnly
                          ? () => {}
                          : (e) =>
                              handleArgumentChange(
                                arg.id,
                                'type',
                                e.target.value
                              )
                      }
                      disabled={readOnly}
                      className={S.selectInput}
                    >
                      {ARGUMENT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Options pour Mode uniquement */}
                  {arg.type === 'Mode' && (
                    <div className={S.optionsSection}>
                      <div className={S.optionsHeader}>
                        <label>Options (clé → valeur)</label>
                        {!readOnly && (
                          <Bouton
                            type={type_btn.secondary}
                            taille="small"
                            cliquer={() => handleAddOption(arg.id)}
                          >
                            <BiPlus size={14} /> Ajouter option
                          </Bouton>
                        )}
                      </div>
                      {(arg.options || []).map((opt, optIdx) => (
                        <div key={optIdx} className={S.optionRow}>
                          <ChampDeTexte
                            name={`option-key-${optIdx}`}
                            placeholder="Clé"
                            type="text"
                            value={opt.key || ''}
                            onChange={
                              readOnly
                                ? () => {}
                                : (value) =>
                                    handleOptionChange(
                                      arg.id,
                                      optIdx,
                                      'key',
                                      value
                                    )
                            }
                            disabled={readOnly}
                          />
                          <ChampDeTexte
                            name={`option-value-${optIdx}`}
                            placeholder="Valeur"
                            type="text"
                            value={opt.value || ''}
                            onChange={
                              readOnly
                                ? () => {}
                                : (value) =>
                                    handleOptionChange(
                                      arg.id,
                                      optIdx,
                                      'value',
                                      value
                                    )
                            }
                            disabled={readOnly}
                          />
                          {!readOnly && (
                            <Bouton
                              type={type_btn.danger}
                              taille="small"
                              rond
                              cliquer={() => handleRemoveOption(arg.id, optIdx)}
                            >
                              <BiTrash size={14} />
                            </Bouton>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!readOnly && (
                  <Bouton
                    type={type_btn.danger}
                    taille="small"
                    rond
                    cliquer={() => handleRemoveArgument(arg.id)}
                    className={S.deleteButton}
                  >
                    <BiTrash size={14} />
                  </Bouton>
                )}
              </div>
            ))}
          </div>
        </Collapse>
      </div>

      {/* Actions */}
      {!readOnly && (
        <div className={S.actions}>
          <Bouton type={type_btn.secondary} cliquer={annuler}>
            <BiX size={18} /> Annuler
          </Bouton>
          <Bouton type={type_btn.primary} cliquer={handleSave}>
            <BiSave size={18} /> {isEditMode ? 'Mettre à jour' : 'Créer'}
          </Bouton>
        </div>
      )}
    </div>
  );
};

FunctionBuilder.propTypes = {
  initialFunction: PropTypes.object,
  isEditMode: PropTypes.bool,
  annuler: PropTypes.func.isRequired,
  sauvegarderFunction: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default FunctionBuilder;
